import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { financeApi } from '../../api/finance';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';

export function GeneralLedgerPage() {
  const [filters, setFilters] = useState<{ accountId?: string; startDate?: string; endDate?: string; reference?: string }>({});
  const [page, setPage] = useState(1);
  const accountsQuery = useQuery({ queryKey: ['finance-accounts'], queryFn: () => financeApi.listAccounts() });
  const ledgerQuery = useQuery({
    queryKey: ['finance-general-ledger', filters, page],
    queryFn: () => financeApi.getGeneralLedger({ accountId: filters.accountId, startDate: filters.startDate, endDate: filters.endDate, reference: filters.reference, page, pageSize: 50 }),
  });

  const lines = ledgerQuery.data?.data ?? [];
  const paginationMeta = ledgerQuery.data?.pagination;
  const totalDebit = lines.reduce((sum, l) => sum + Number(l.debit_amount), 0);
  const totalCredit = lines.reduce((sum, l) => sum + Number(l.credit_amount), 0);

  const updateFilters = (updater: (f: typeof filters) => typeof filters) => {
    setFilters(updater);
    setPage(1); // real reset - a new filter genuinely changes the result set, so staying on page 4 of a different query would be wrong
  };

  return (
    <div className="page">
      <PageHeader title="General Ledger" actions={<a href={financeApi.generalLedgerPdfUrl(filters)} target="_blank" rel="noreferrer">Download PDF</a>} />

      <section className="card">
        <div className="inline-form">
          <select value={filters.accountId ?? ''} onChange={(e) => updateFilters((f) => ({ ...f, accountId: e.target.value || undefined }))}>
            <option value="">All Accounts</option>
            {accountsQuery.data?.data.map((a) => <option key={a.id} value={a.id}>{a.account_code} - {a.account_name}</option>)}
          </select>
          <input type="date" value={filters.startDate ?? ''} onChange={(e) => updateFilters((f) => ({ ...f, startDate: e.target.value || undefined }))} />
          <input type="date" value={filters.endDate ?? ''} onChange={(e) => updateFilters((f) => ({ ...f, endDate: e.target.value || undefined }))} />
          <input type="search" placeholder="Reference" value={filters.reference ?? ''} onChange={(e) => updateFilters((f) => ({ ...f, reference: e.target.value || undefined }))} />
        </div>

        <DataTable
          rows={lines}
          rowKey={(l) => `${l.journal_reference}-${l.account_code}-${l.debit_amount}-${l.credit_amount}`}
          emptyMessage="No posted transactions match your filters."
          pagination={paginationMeta ? { ...paginationMeta, onPageChange: setPage } : undefined}
          columns={[
            { header: 'Date', render: (l) => l.entry_date, sortValue: (l) => l.entry_date },
            { header: 'Reference', render: (l) => l.journal_reference, sortValue: (l) => l.journal_reference },
            { header: 'Account', render: (l) => `${l.account_code} - ${l.account_name}`, sortValue: (l) => l.account_code },
            { header: 'Description', render: (l) => l.line_description ?? l.entry_description },
            { header: 'Debit', render: (l) => Number(l.debit_amount) > 0 ? Number(l.debit_amount).toLocaleString() : '-', sortValue: (l) => Number(l.debit_amount) },
            { header: 'Credit', render: (l) => Number(l.credit_amount) > 0 ? Number(l.credit_amount).toLocaleString() : '-', sortValue: (l) => Number(l.credit_amount) },
            { header: 'Project', render: (l) => l.project_name ?? '-' },
          ]}
        />
        {lines.length > 0 && <p><strong>Page Total: Debit {totalDebit.toLocaleString()} · Credit {totalCredit.toLocaleString()}</strong></p>}
      </section>
    </div>
  );
}
